import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { editWorkflowForm } from "../../shared/services/workflow.services";
import { getNotification } from "../../utils/common";
import { icon, label, notifyIcon, number, quote } from "../../config/constants";

// Sorts teams alphabetically by QueueName, grouped by QueueId
const getSortedTeams = (teams, selectedTeams) => {
  if (!teams || teams.length === 0) return [];

  const selectedIds = new Set((selectedTeams || []).map((t) => t?.TeamId));

  // Separate selected and unselected teams
  const selected = teams.filter((t) => selectedIds.has(t?.TeamId));
  const unselected = teams.filter((t) => !selectedIds.has(t?.TeamId));

  // Sort selected teams to maintain their selection order
  selected.sort((a, b) => {
    const indexA = selectedTeams.findIndex((t) => t?.TeamId === a?.TeamId);
    const indexB = selectedTeams.findIndex((t) => t?.TeamId === b?.TeamId);
    return indexA - indexB;
  });

  unselected.sort((a, b) => {
    const queueNameA = a?.QueueName || "";
    const queueNameB = b?.QueueName || "";

    const nameComparison = queueNameA.localeCompare(queueNameB);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    const queueIdA = a?.QueueId || 0;
    const queueIdB = b?.QueueId || 0;
    if (queueIdA !== queueIdB) {
      return queueIdA - queueIdB;
    }

    return (a?.TeamName || "").localeCompare(b?.TeamName || "");
  });

  // Return with "Selected Teams" group first, then all other groups sorted
  return [
    ...selected.map((item) => ({ ...item, QueueName: "Selected Teams" })),
    ...unselected
  ];
};

// Checks if a team is selected
const isTeamChecked = (teamId, selectedTeams) => {
  return selectedTeams?.some((team) => team?.TeamId === teamId) || false;
};

// Renders custom tag for MultiSelect
const tagRender = (tagData, li, selectedTeams) => {
  if (selectedTeams?.length > number.ONE) {
    if (tagData?.data?.[number.ZERO] === selectedTeams?.[number.ZERO]) {
      const selectedTeamsList = selectedTeams.map((item) => item?.TeamName).join(", ");
      return React.cloneElement(li, li.props, [
        <span title={selectedTeamsList}>{`${selectedTeams.length} teams selected`}</span>
      ]);
    }
    return null;
  }
  const tagInfo = tagData?.data?.[number.ZERO];
  return React.cloneElement(li, li.props, [
    <span className="text-truncate d-inline-block w-100" title={tagInfo?.TeamName}>
      {tagInfo?.TeamName}
      {li.props?.onRemove && (
        <span
          className="remove-search-button d-flex justify-content-center align-items-center position-absolute cursor-pointer"
          onClick={() => li.props?.onRemove?.(tagData)}
          title={label.REMOVE}>
          {icon.CLEAR}
        </span>
      )}
    </span>,
    li.props?.children,
  ]);
};

// Renders custom item for MultiSelect dropdown
const itemRender = (li, itemProps, selectedTeams) => {
  const { dataItem } = itemProps;
  const isSelected = isTeamChecked(dataItem?.TeamId, selectedTeams);
  return React.cloneElement(li, li.props, (
    <span className="d-flex align-items-center w-100" title={dataItem?.TeamName}>
      <input className="mr-2" id={`team-checkbox-${dataItem?.TeamId}`} type="checkbox" checked={isSelected} onChange={() => {}} />
      <span className="text-truncate">{dataItem?.TeamName}</span>
    </span>
  ));
};

/**
 * Custom Privacy MultiSelect Component
 * Handles all MultiSelect-related functionality for custom privacy selection
 * @author Bhavana
 */
const CustomPrivacyMultiSelect = ({
  formId,
  isNewForm,
  currentFormData,
  privacyLevel,
  setPrivacyLevel,
  onPrivacyChange,
  setAllowedProjectIds,
  teamData = [],
  selectedTeams,
  setSelectedTeams,
  setIsUsingCustomPrivacy,
}) => {
  const dispatch = useDispatch();
  const [filteredTeams, setFilteredTeams] = useState([]);

  /**
   * Initialize filtered teams when data or selection changes
   * Similar to DescriptionSetting.js approach
   * @author Bhavana
   */
  useEffect(() => {
    const sortedTeams = getSortedTeams(teamData, selectedTeams);
    setFilteredTeams(sortedTeams);
  }, [teamData, selectedTeams]);

  /**
   * Keep isUsingCustomPrivacy in sync with selectedTeams
   * @author Bhavana
   */
  useEffect(() => {
    if (privacyLevel?.value === number.FOUR) {
      setIsUsingCustomPrivacy(true);
    } else {
      const hasSelections = selectedTeams && selectedTeams.length > number.ZERO;
      setIsUsingCustomPrivacy(hasSelections);
    }
  }, [selectedTeams?.length, privacyLevel?.value, setIsUsingCustomPrivacy]);

  /**
   * Handles team selection change and updates privacy settings
   * @author Bhavana
   */
  const handleTeamChange = useCallback(async (newTeams) => {
    setSelectedTeams(newTeams);

    if (privacyLevel?.value === number.FOUR) {
      setIsUsingCustomPrivacy(true);
    } else {
      setIsUsingCustomPrivacy(newTeams?.length > number.ZERO);
    }

    const allowedProjectIds = [...new Set(
      (newTeams || []).map((team) => parseInt(team?.TeamId)).filter((id) => !isNaN(id))
    )];

    setAllowedProjectIds(allowedProjectIds);

    if (isNewForm || !formId) {
      setPrivacyLevel({
        label: `Custom (${allowedProjectIds.length} Selected)`,
        value: number.FOUR,
        isCustom: true,
      });
      onPrivacyChange?.(number.FOUR);
      return;
    }

    try {
      const response = await dispatch(editWorkflowForm({
        id: formId,
        workflowId: currentFormData?.workflowId,
        privacy: number.FOUR,
        allowedProjectIds,
      }));

      setPrivacyLevel({
        label: `Custom (${allowedProjectIds.length} Selected)`,
        value: number.FOUR,
        isCustom: true,
      });

      if (response) {
        getNotification(quote.ON_UPDATE, notifyIcon.SUCCESS_ICON);
      }
    } catch (error) {
      console.error("Error updating form custom privacy:", error);
      getNotification(quote.ON_ERROR, notifyIcon.ERROR_ICON);
    }
  }, [formId, isNewForm, currentFormData, dispatch, privacyLevel, setPrivacyLevel, onPrivacyChange, setAllowedProjectIds, setIsUsingCustomPrivacy, setSelectedTeams]);

  /**
   * Handles filter change for team search
   * Maintains proper sorting (QueueName) and grouping (QueueId) after filtering
   * @author Bhavana
   */
  const handleFilterChange = useCallback((event) => {
    const sortedTeams = getSortedTeams(teamData, selectedTeams);
    if (event.filter.value) {
      const filtered = filterBy(sortedTeams, event.filter);
      // Ensure filtered results maintain sorting: QueueName → QueueId → TeamName
      const sortedFiltered = filtered.sort((a, b) => {
        // Keep "Selected Teams" at the top
        if (a.QueueName === "Selected Teams" && b.QueueName !== "Selected Teams") return -1;
        if (a.QueueName !== "Selected Teams" && b.QueueName === "Selected Teams") return 1;

        // Sort by QueueName (alphabetical)
        const queueNameA = a?.QueueName || "";
        const queueNameB = b?.QueueName || "";
        const nameComparison = queueNameA.localeCompare(queueNameB);
        if (nameComparison !== 0) {
          return nameComparison;
        }

        // Sort by QueueId (grouping)
        const queueIdA = a?.QueueId || 0;
        const queueIdB = b?.QueueId || 0;
        if (queueIdA !== queueIdB) {
          return queueIdA - queueIdB;
        }

        // Sort by TeamName within the same queue
        return (a?.TeamName || "").localeCompare(b?.TeamName || "");
      });
      setFilteredTeams(sortedFiltered);
    } else {
      setFilteredTeams(sortedTeams);
    }
  }, [teamData, selectedTeams]);

  return (
    <MultiSelect
      data={filteredTeams}
      value={selectedTeams}
      className="w-100"
      textField="TeamName"
      dataItemKey="TeamId"
      groupField="QueueName"
      onChange={(e) => handleTeamChange(e?.target?.value)}
      tagRender={(tagData, li) => tagRender(tagData, li, selectedTeams)}
      itemRender={(li, itemProps) => itemRender(li, itemProps, selectedTeams)}
      placeholder="Select teams..."
      autoClose={false}
      filterable={true}
      onFilterChange={handleFilterChange}
      style={{ minWidth: "280px" }}
    />
  );
};

export default CustomPrivacyMultiSelect;
